import { useEffect, useState } from "react";
import { Film, FileDown, FileCheck2 } from "lucide-react";
import {
  parseStudioPackage,
  recordingBrief,
  downloadText,
  type StudioPackage,
} from "@/lib/cpd-studio";
import "@/styles/cpd-studio.css";

export function CpdStudio({
  onImport,
}: {
  onImport: (pkg: StudioPackage, body: string) => void;
}) {
  const [topic, setTopic] = useState("");
  const [speaker, setSpeaker] = useState("");
  const [source, setSource] = useState<File | null>(null);
  const [preview, setPreview] = useState("");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  useEffect(() => {
    if (!source) {
      setPreview("");
      return;
    }
    const url = URL.createObjectURL(source);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [source]);
  return (
    <section className="pb-cpd-studio" aria-labelledby="studio-heading">
      <div className="pb-cpd-studio-intro">
        <p>01 Record · 02 Edit · 03 Review · 04 Release</p>
        <h2 id="studio-heading">Small sessions. A considered production.</h2>
        <span>
          Prepare the specialist’s brief, review a local recording, then bring
          the finished session back from Studio.
        </span>
      </div>
      <div className="pb-cpd-studio-grid">
        <div>
          <h3>
            <FileDown size={18} aria-hidden="true" />
            Prepare a recording brief
          </h3>
          <label>
            One practical GP question
            <input
              value={topic}
              maxLength={90}
              onChange={(event) => setTopic(event.target.value)}
              placeholder="Considerations when changing antihypertensives"
            />
          </label>
          <label>
            Specialist’s name
            <input
              value={speaker}
              maxLength={120}
              onChange={(event) => setSpeaker(event.target.value)}
              placeholder="Name to appear in the brief"
            />
          </label>
          <button
            type="button"
            disabled={!topic.trim() || !speaker.trim()}
            onClick={() => {
              downloadText(
                "five-minute-cpd-recording-brief.md",
                recordingBrief(topic, speaker),
              );
              setMessage("Recording brief downloaded.");
            }}
          >
            Download recording brief
          </button>
        </div>
        <div>
          <h3>
            <Film size={18} aria-hidden="true" />
            Check the source recording
          </h3>
          <p>
            Local preview only. The recording stays on this device. Editing
            takes place in your existing Remotion Studio.
          </p>
          <label>
            Choose an iPhone recording
            <input
              type="file"
              accept="video/*,.mov,.mp4"
              onChange={(event) => {
                setError("");
                const file = event.target.files?.[0];
                if (!file) return;
                if (
                  !file.type.startsWith("video/") &&
                  !/\.(mov|mp4)$/i.test(file.name)
                ) {
                  setError("Choose a video recording.");
                  return;
                }
                setSource(file);
              }}
            />
          </label>
          {preview ? (
            <>
              <video
                className="pb-cpd-source-preview"
                key={preview}
                src={preview}
                controls
                preload="metadata"
                onError={() =>
                  setError(
                    "This browser cannot preview the recording. Keep the original and open it in Studio for conversion.",
                  )
                }
              />
              <button type="button" onClick={() => setSource(null)}>
                Clear recording
              </button>
            </>
          ) : null}
        </div>
        <div>
          <h3>
            <FileCheck2 size={18} aria-hidden="true" />
            Import the reviewed session
          </h3>
          <p>
            Choose the version 1 Studio JSON package. Its review details and
            sources become visible session notes. Import fills the draft form
            below; select the matching specialist before saving.
          </p>
          <label>
            Reviewed session package
            <input
              type="file"
              accept="application/json,.json"
              onChange={async (event) => {
                const file = event.target.files?.[0];
                event.target.value = "";
                setError("");
                setMessage("");
                if (!file) return;
                if (file.size > 32_768) {
                  setError(
                    "The session package must be smaller than 32 KB. Choose the JSON file, not a recording.",
                  );
                  return;
                }
                try {
                  const { pkg, body } = parseStudioPackage(
                    JSON.parse(await file.text()),
                  );
                  onImport(pkg, body);
                  setMessage(
                    `Package loaded for ${pkg.specialistName}. Confirm the matching specialist and review the draft below.`,
                  );
                } catch (caught) {
                  setError(
                    caught instanceof Error
                      ? `Package not imported: ${caught.message}`
                      : "The package could not be read.",
                  );
                }
              }}
            />
          </label>
          <p>
            Speaker approval, captions, patient-free content, current review
            dates and source links are required in the package. The editor must
            verify these attestations before release.
          </p>
        </div>
      </div>
      {error ? (
        <p role="alert" className="pb-loop-error">
          {error}
        </p>
      ) : null}
      {message ? (
        <p role="status" className="pb-loop-success">
          {message}
        </p>
      ) : null}
    </section>
  );
}
